import cx from 'classnames';
import React from 'react';

import {ThemeConsumer} from '../../../themes/ThemeContext';
import type {StandardSizeModifier} from '../../../types/sizes';
import type {ColorScheme} from '../../../types/variants';
import {Row} from '../../modules/List/Row';
import type {InteractiveElementProps} from '../InteractiveElement/InteractiveElement';
import {InteractiveElement} from '../InteractiveElement/InteractiveElement';

import styles from './ListBoxLink.module.scss';

export const ListBoxLink: React.FC<
    InteractiveElementProps & {
        children: React.ReactNode;
        colorScheme?: ColorScheme;
        hasBorder?: boolean;
        isDisabled?: boolean;
        isHovered?: boolean;
        sizeModifier?: StandardSizeModifier;
        // Needs to be defined explicitly for TS to get the typing
        onClick?: (
            e:
                | React.MouseEvent<HTMLButtonElement>
                | React.MouseEvent<HTMLAnchorElement>
                | React.MouseEvent<HTMLDivElement>,
        ) => void;
    }
> = ({
    children,
    className,
    colorScheme,
    hasBorder = true,
    isDisabled,
    isHovered,
    sizeModifier = 'large',
    href,
    onClick,
    ...props
}) => {
    // Since InteractiveElement can either be a link or a button we have to be
    // careful when setting properties.
    // TODO: This logic should be moved to InteractiveElement.
    const htmlTag = !href && !onClick ? 'div' : undefined;
    const isAnchor = Boolean(href) && href !== '#';
    const willBecomeLink = isAnchor && !isDisabled;
    const type = htmlTag === 'div' || willBecomeLink ? undefined : 'button';
    return (
        <ThemeConsumer>
            {({colorScheme: defaultColorScheme}) => (
                <InteractiveElement
                    className={cx(
                        styles.wrapper,
                        styles[colorScheme ?? defaultColorScheme],
                        sizeModifier && styles[sizeModifier],
                        {[styles.disabled]: isDisabled},
                        {[styles.hovered]: isHovered},
                        className,
                    )}
                    disabled={isDisabled}
                    href={href}
                    htmlTag={htmlTag}
                    onClick={onClick}
                    type={type}
                    {...props}
                >
                    <Row
                        spacing="none"
                        className={cx(styles.inner, {
                            [styles.border]: hasBorder,
                        })}
                    >
                        {children}
                    </Row>
                </InteractiveElement>
            )}
        </ThemeConsumer>
    );
};

export type ListBoxLinkProps = React.ComponentProps<typeof ListBoxLink>;
